/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Table, TreeSelect, message, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableProps } from 'antd/lib/table';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { queryCache, useMutation, useQuery } from 'react-query';

import IconButton from '~components/_common/IconButton/IconButton';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { fadeInKeyframes } from '~src/styles/animations';
import { DB_STATE } from '~src/types/enums';
import { useUserSettings } from '~src/hooks/api-hooks/useUserSettings';
import { useArchiveUsers } from '~src/hooks/api-hooks/useArchiveUsers';
import {
  getModuleAdmins,
  setModuleAdmin,
  revokeModuleAdmin,
  ModuleAdminUser,
} from '~src/services/api/modules';
import { useProjectId } from '~src/hooks/useProjectId';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { tabStyleFn } from './tabs.util';

const { confirm } = Modal;

export interface ModulesTabProps {
  loading?: boolean;
}

export const ModulesTab: React.FC<ModulesTabProps> = ({ loading }) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const tabStyles = useStyles(tabStyleFn);

  const isSuperuser = useIsSuperuser();
  const archiveUsers = useArchiveUsers();
  const userSettings = useUserSettings();
  const projectId = useProjectId();

  const currentUserId = userSettings.data?.user_id;

  // --- API: Fetch Module Admins ---
  const { data: moduleAdmins, isLoading } = useQuery(
    ['moduleAdmins', projectId],
    () => getModuleAdmins({ projectId: String(projectId) }),
    {
      enabled: !!projectId && isSuperuser,
      refetchOnWindowFocus: false,
    }
  );

  // --- API: Add Module Admin ---
  const [doSetModuleAdmin] = useMutation(setModuleAdmin, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'module-admin-add-failed' }));
    },
    onSuccess: () => {
      queryCache.invalidateQueries(['moduleAdmins', projectId]);
      message.success(intl.formatMessage({ id: 'module-admin-added' }));
      setSelectedValue(undefined);
    },
  });

  // --- API: Remove Module Admin ---
  const [doRevokeModuleAdmin] = useMutation(revokeModuleAdmin, {
    onSuccess: () => {
      queryCache.invalidateQueries(['moduleAdmins', projectId]);
      message.success(intl.formatMessage({ id: 'module-admin-removed' }));
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'module-admin-remove-failed' }));
    },
  });

  // --- State ---
  const [selectedValue, setSelectedValue] = useState<
    string | number | undefined
  >();

  // --- Data Preparation ---
  const tableData = useMemo(() => {
    return moduleAdmins?.map((item) => ({ ...item, key: item.id }));
  }, [moduleAdmins]);

  const handleTreeSelectChange = (value: string | number) => {
    setSelectedValue(value);
    doSetModuleAdmin({
      userId: String(value),
      projectId: String(projectId),
    });
  };

  const addModuleAdminData = useMemo(() => {
    const groupedUsers = _.groupBy(
      archiveUsers,
      (user) => user.workgroup.group_initials
    );

    return _.map(groupedUsers, (users, groupInitials) => {
      return {
        title: (
          <div>
            <span style={{ color: '#c1c1c1', fontSize: '12px' }}>
              {groupInitials + ' - ' + users[0].workgroup.group_name}
            </span>
          </div>
        ),
        value: groupInitials,
        selectable: false,
        children: users.map((user) => {
          const selectable = !tableData?.some((item) => item.id === user.id);
          return {
            title: (
              <div>
                <span style={{ color: selectable ? '' : 'grey' }}>
                  {user.user_initials + ' - ' + user.full_name}{' '}
                </span>
                {!selectable && (
                  <FontAwesomeIcon
                    color={'#c1c1c1'}
                    size={'sm'}
                    icon={['far', 'user-tie']}
                  />
                )}
              </div>
            ),
            selectable,
            value: user.id,
          };
        }),
      };
    });
  }, [archiveUsers, tableData]);

  const getColorRow = (record: any) =>
    Number(record.state) === DB_STATE.REMOVED ? '#ffd4d9' : '';

  const filterFunc = (inputValue: string, treeNode: any) => {
    if (inputValue && treeNode?.value) {
      return (
        treeNode.value
          .toString()
          .toLowerCase()
          .includes(inputValue.toLowerCase()) ?? false
      );
    }
    return false;
  };

  const handleDeleteClick = (user: ModuleAdminUser) => {
    const isSelf = currentUserId !== undefined && currentUserId === user.id;

    confirm({
      title: intl.formatMessage({ id: 'delete-module-admin' }),
      content: intl.formatMessage(
        { id: 'delete-module-admin-confirm' },
        {
          name: isSelf
            ? intl.formatMessage({ id: 'yourself' })
            : user.full_name,
        }
      ),
      okText: intl.formatMessage({ id: 'delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk() {
        doRevokeModuleAdmin({
          projectId: String(projectId),
          userId: String(user.id),
        });
      },
    });
  };

  const columns: TableProps<ModuleAdminUser>['columns'] = [
    {
      key: 'remove',
      title: '',
      dataIndex: 'remove',
      width: 24,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
          textAlign: 'center',
        },
      }),
      render: (title, node) => {
        const isSelf = currentUserId !== undefined && currentUserId === node.id;

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(node);
            }}
            css={style.actionContainer}>
            <IconButton
              icon={isSelf ? ['far', 'check'] : ['far', 'circle-xmark']}
              customStyle={[tabStyles.folderIcon, style.redIcon]}
              containerStyle={style.iconRemove}
              backgroundOff
              tooltip={intl.formatMessage({ id: 'delete-module-admin' })}
            />
          </div>
        );
      },
    },
    {
      key: 'workgroup',
      title: intl.formatMessage({ id: 'workgroup' }),
      dataIndex: 'workgroup',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <span color={'#1f5fa6'}>{title.group_initials}</span>,
    },
    {
      key: 'initials',
      title: intl.formatMessage({ id: 'initials' }),
      dataIndex: 'user_initials',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <span color={'#1f5fa6'}>{title}</span>,
    },
    {
      key: 'name',
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: 'full_name',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <span color={'#1f5fa6'}>{title}</span>,
    },
  ];

  return (
    <div css={style.container}>
      <div css={style.rowContainer}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <h3 css={style.heading}>
            {intl.formatMessage({ id: 'module-admins' })}
          </h3>
          <IconPopup
            customStyle={{ fontSize: '130%' }}
            headerId={'module-admins-help-header'}
            messageId={'module-admins-help-body'}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '20px 0 10px 0',
          }}>
          <span css={{ display: 'inline-block', width: 170 }}>
            <span css={{ fontSize: '16px', marginRight: '6px' }}>
              {intl.formatMessage({ id: 'module-admin' })}
            </span>
            <FontAwesomeIcon
              color={'#c1c1c1'}
              size={'1x'}
              icon={['far', 'user-tie']}
            />
          </span>
          <TreeSelect
            filterTreeNode={filterFunc}
            showSearch
            className="my-tree-select"
            style={{ width: 300 }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            treeData={addModuleAdminData}
            placeholder={intl.formatMessage({ id: 'add-module-admin' })}
            treeDefaultExpandAll
            value={selectedValue}
            onChange={handleTreeSelectChange}
          />
        </div>
        <div css={[style.rowContainer]}>
          <Table<ModuleAdminUser>
            showSorterTooltip={false}
            bordered
            pagination={false}
            scroll={{ y: '22vh', x: 200 }}
            loading={loading || isLoading}
            columns={columns}
            dataSource={tableData}
          />
        </div>
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
  iconRemove: {},
  rowContainer: { marginBottom: '8px' },
  tag: {
    marginRight: 3,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
  actionContainer: {
    cursor: 'pointer',
  },
  icons: {
    color: theme.colors.text.primary,
    fontSize: '16px',
  },
  redIcon: {
    color: theme.colors.red,
  },
  heading: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '15px',
  },
});
